import { useEffect, useState } from 'react'
import { FileText, Presentation } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/Text'
import { knowledgeApi } from '@/lib/api'
import { cn } from '@/lib/utils'

type PreviewMode = 'slide' | 'text'

const SLIDE_EXTENSIONS: Array<string> = ['.pptx', '.ppsx']

interface SlideDocumentPreviewProps {
  documentToken: string
  title: string
  pageNumber: number
  extractedText?: string | null
  className?: string
}

export function isSlideDocument(
  mimeType?: string | null,
  filename?: string | null,
): boolean {
  const mime = (mimeType || '').toLowerCase()
  if (mime.includes('presentation') || mime.includes('powerpoint')) {
    return true
  }
  const name = (filename || '').toLowerCase()
  return SLIDE_EXTENSIONS.some((extension) => name.endsWith(extension))
}

/**
 * A PPTX slide shown as a laid-out slide instead of as extracted text.
 *
 * The backend builds the markup from the deck's own shape geometry, so it is our
 * own generated HTML rather than third-party content. It is still framed under a
 * fully restrictive `sandbox` — no scripts, opaque origin — because the slide
 * text itself comes from an uploaded file we do not control.
 *
 * Fidelity is a readable facsimile, not pixel parity: charts and SmartArt
 * degrade to placeholders, so the extracted-text view stays one click away.
 */
export function SlideDocumentPreview(props: SlideDocumentPreviewProps) {
  const { documentToken, title, pageNumber, extractedText, className } = props

  const [html, setHtml] = useState<string | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [mode, setMode] = useState<PreviewMode>('slide')

  useEffect(() => {
    let active = true
    setHtml(null)
    setLoadFailed(false)

    knowledgeApi
      .getDocumentSlideHtml(documentToken, pageNumber)
      .then((markup) => {
        if (active) {
          if (markup) {
            setHtml(markup)
          } else {
            setLoadFailed(true)
          }
        }
      })
      .catch(() => {
        if (active) {
          setLoadFailed(true)
        }
      })

    return () => {
      active = false
    }
  }, [documentToken, pageNumber])

  const showText = mode === 'text' || loadFailed

  return (
    <div
      className={cn('flex min-h-0 flex-1 flex-col gap-3', className)}
      data-testid="slide-document-preview"
    >
      <div className="flex shrink-0 items-center justify-between gap-3">
        <Text size="xs" color="muted">
          {showText ? 'Text as the model reads it' : `Slide ${pageNumber}`}
        </Text>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant={showText ? 'ghost' : 'outline'}
            size="sm"
            className="h-8"
            disabled={loadFailed}
            data-testid="slide-document-mode-slide"
            onClick={() => setMode('slide')}
          >
            <Presentation className="mr-1.5 h-3.5 w-3.5" />
            Slide view
          </Button>
          <Button
            type="button"
            variant={showText ? 'outline' : 'ghost'}
            size="sm"
            className="h-8"
            data-testid="slide-document-mode-text"
            onClick={() => setMode('text')}
          >
            <FileText className="mr-1.5 h-3.5 w-3.5" />
            Extracted text
          </Button>
        </div>
      </div>

      {showText ? (
        <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-border bg-white p-6 text-black shadow-sm dark:bg-zinc-100">
          {loadFailed ? (
            <Text size="sm" className="mb-4 text-amber-700">
              This slide could not be rendered, so this is the extracted text.
            </Text>
          ) : null}
          <pre
            className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-zinc-800"
            data-testid="slide-document-text"
          >
            {extractedText || 'No text extracted for this slide.'}
          </pre>
          <div className="mt-8 flex justify-between text-xs text-zinc-500">
            <span>{title}</span>
            <span>p. {pageNumber}</span>
          </div>
        </div>
      ) : html == null ? (
        <div className="min-h-0 flex-1 rounded-xl border border-border bg-white p-4 dark:bg-zinc-100">
          <Skeleton className="aspect-video w-full" />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <iframe
            title={`${title} — slide ${pageNumber}`}
            srcDoc={html}
            // The rendition is a fixed-aspect canvas that scales to the frame
            // width, so the frame only needs to match that aspect to avoid
            // letterboxing or an inner scrollbar.
            className="aspect-video w-full rounded-xl border border-border bg-white shadow-sm"
            sandbox=""
            referrerPolicy="no-referrer"
            scrolling="no"
            data-testid="slide-document-frame"
          />
        </div>
      )}
    </div>
  )
}
